import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, ArrowRight, KanbanSquare, AlertTriangle, Inbox } from "lucide-react";
import api from "@/lib/api";
import { PageHeader, Card, Spinner, Empty } from "@/components/common";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { StatusPill, PrioPill, TypePill, PRIORITIES, projectTypeForBranche, fmtDate, inputCls } from "./leadUtils";
import { useI18n } from "@/context/I18nContext";

const SUMMARY = [
  ["gesamt", "Anfragen gesamt"], ["neu", "Neu"], ["in_bearbeitung", "In Bearbeitung"],
  ["warten_auf_kunde", "Warten auf Kunde"], ["angebot_vorbereitet", "Angebot vorbereitet"],
  ["erledigt", "Erledigt"], ["dringend", "Dringend"],
];
const PROCESS_FILTERS = ["alle", "neu", "in_bearbeitung", "rueckfrage_offen", "angebot_vorbereitet", "warten_auf_kunde", "erledigt", "archiviert"];
const RESULT_FILTERS = ["alle", "auftrag_erhalten", "nicht_erhalten", "verkauf_abgeschlossen", "reserviert", "in_aufgabe_umgewandelt", "in_angebot_umgewandelt"];

const EMPTY = {
  lead_type: "kunden_anfrage", client_id: "", company: "", name: "", email: "", phone: "", subject: "", message: "", status: "neu",
  priority: "mittel", source: "", campaign: "", brevo_contact_id: "", brevo_list: "", interest: "",
  callback_wanted: "", due_date: "", assignee: "", possible_service: "", estimated_value: "", internal_note: "", result_status: "noch_offen",
};

export default function Leads() {
  const { t } = useI18n();
  const nav = useNavigate();
  const newLeadBtnRef = useRef(null);
  const leadTypeInput = useRef(null);
  const savingRef = useRef(false);
  const [leads, setLeads] = useState(null);
  const [clients, setClients] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [filter, setFilter] = useState("alle");
  const [resultFilter, setResultFilter] = useState("alle");
  const [typeFilter, setTypeFilter] = useState("alle");
  const [q, setQ] = useState("");
  const [params, setParams] = useSearchParams();
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/leads").then((r) => setLeads(r.data));
  useEffect(() => {
    load();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/leads/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (params.get("new") === "1") { setShowNew(true); params.delete("new"); setParams(params, { replace: true }); }
  }, [params, setParams]);

  const counts = useMemo(() => {
    const c = { gesamt: 0, neu: 0, in_bearbeitung: 0, warten_auf_kunde: 0, angebot_vorbereitet: 0, erledigt: 0, dringend: 0 };
    (leads || []).forEach((l) => {
      c.gesamt++;
      if (c[l.status] !== undefined) c[l.status]++;
      if (l.priority === "dringend" && !["erledigt", "archiviert"].includes(l.status)) c.dringend++;
    });
    return c;
  }, [leads]);

  const isNeu = form.lead_type === "neukunden_lead";
  const formClient = clients.find((c) => c.id === form.client_id);
  const formPtype = isNeu ? "kewag" : projectTypeForBranche(formClient?.branche);
  const formProcessKeys = cfg?.project_types?.[formPtype]?.process || PROCESS_FILTERS.slice(1);
  const formResultKeys = cfg?.project_types?.[formPtype]?.result || RESULT_FILTERS.slice(1);

  if (!leads || !cfg) return <Spinner />;

  const plabel = (k) => t(cfg.process_labels[k] || k);
  const rlabel = (k) => t(cfg.result_labels[k] || k);

  const changeStatus = async (l, status) => {
    await api.put(`/leads/${l.id}`, { status });
    toast.success(t("Bearbeitungsstatus aktualisiert"));
    load();
  };

  // ---- Validierung Neue Anfrage ----
  const errs = [];
  const warns = [];
  if (showNew) {
    if (!isNeu && !form.client_id) errs.push(t("Kunde / Projekt fehlt."));
    if (isNeu && !form.company.trim() && !form.name.trim()) errs.push(t("Firma / Interessent fehlt."));
    if (!form.name.trim()) errs.push(t("Name fehlt."));
    if (!form.email.trim() && !form.phone.trim()) errs.push(t("E-Mail oder Telefon erforderlich."));
    if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.push(t("E-Mail ist ungültig."));
    if (!form.subject.trim()) errs.push(t("Betreff fehlt."));
    if (!form.message.trim()) errs.push(t("Nachricht fehlt."));
    if (!form.status) errs.push(t("Bearbeitungsstatus fehlt."));
    const dup = !isNeu && (leads || []).find((l) => l.client_id === form.client_id && ((form.email.trim() && (l.email || "").toLowerCase() === form.email.trim().toLowerCase()) || (form.phone.trim() && l.phone === form.phone.trim())));
    if (dup) warns.push(t("Möglicher Doppel-Eintrag: gleiche E-Mail/Telefon für diesen Kunden vorhanden."));
  }

  const onClient = (id) => {
    const c = clients.find((x) => x.id === id);
    const pt = projectTypeForBranche(c?.branche);
    const procKeys = cfg.project_types?.[pt]?.process || [];
    setForm((f) => ({ ...f, client_id: id, status: procKeys.includes(f.status) ? f.status : "neu" }));
  };

  const save = async () => {
    if (errs.length) { toast.error(t("Bitte Pflichtfelder prüfen.")); return; }
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const payload = { ...form, client_id: isNeu ? null : form.client_id, estimated_value: form.estimated_value === "" ? null : Number(form.estimated_value), due_date: form.due_date || null };
      await api.post("/leads", payload);
      toast.success(t("Anfrage erstellt"));
      setShowNew(false); setForm(EMPTY); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("Konnte Anfrage nicht speichern."));
    } finally { savingRef.current = false; setSaving(false); }
  };

  const quickTask = async (l) => {
    try { await api.post(`/leads/${l.id}/convert-task`, { set_status: "in_bearbeitung" }); toast.success(t("Aufgabe erstellt")); load(); }
    catch { toast.error(t("Konnte Aufgabe nicht erstellen.")); }
  };

  // ---- Filtern ----
  const ql = q.trim().toLowerCase();
  const shown = leads.filter((l) => {
    if (filter !== "alle" && l.status !== filter) return false;
    if (resultFilter !== "alle" && l.result_status !== resultFilter) return false;
    if (typeFilter !== "alle" && l.lead_type !== typeFilter) return false;
    if (ql) {
      const hay = `${l.name} ${l.company_name} ${l.subject} ${l.email} ${l.message}`.toLowerCase();
      if (!hay.includes(ql)) return false;
    }
    return true;
  });

  return (
    <div className="fade-in">
      <PageHeader title={t("Anfragen")} subtitle={t("Erfassen, zuordnen, bearbeiten und intern auswerten — ein echtes Arbeits-System.")}>
        <button ref={newLeadBtnRef} onClick={() => setShowNew(true)} data-testid="new-lead-btn"
          className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2.5 px-5 rounded-lg transition-colors inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> {t("Neue Anfrage")}
        </button>
      </PageHeader>

      {/* Zusammenfassung */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="lead-summary">
        {SUMMARY.map(([k, lbl]) => (
          <Card key={k} className="p-4" data-testid={`lead-stat-${k}`}>
            <div className="text-2xl font-black tracking-tight">{counts[k]}</div>
            <div className="text-[11px] text-gray-400 mt-0.5">{t(lbl)}</div>
          </Card>
        ))}
      </div>

      {/* Typ-Filter */}
      <div className="flex gap-2 mb-4 flex-wrap" data-testid="lead-type-filter">
        {[["alle", "Alle Typen"], ...Object.entries(cfg.lead_types)].map(([k, v]) => (
          <button key={k} onClick={() => setTypeFilter(k)} data-testid={`lead-typefilter-${k}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${typeFilter === k ? "bg-white text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>
            {t(v)}
          </button>
        ))}
      </div>

      {/* Suche + Filter */}
      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-0">
          <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-search"
            placeholder={t("Suche nach Name, Kunde, Betreff, E-Mail...")} className={`${inputCls} pl-9`} />
        </div>
        <select value={resultFilter} onChange={(e) => setResultFilter(e.target.value)} data-testid="lead-result-filter"
          className="bg-black border border-white/10 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#A3E635]">
          {RESULT_FILTERS.map((r) => <option key={r} value={r}>{r === "alle" ? t("Ergebnis (intern): Alle") : rlabel(r)}</option>)}
        </select>
      </div>
      <div className="flex gap-2 mb-5 flex-wrap">
        {PROCESS_FILTERS.map((s) => (
          <button key={s} onClick={() => setFilter(s)} data-testid={`lead-filter-${s}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${filter === s ? "bg-[#A3E635] text-black" : "bg-white/5 text-gray-400 hover:text-white"}`}>
            {s === "alle" ? t("Alle") : plabel(s)}
          </button>
        ))}
      </div>

      {shown.length === 0 ? <Empty text={t("Keine Anfragen in dieser Ansicht.")} /> : (
        <>
          {/* Desktop-Tabelle */}
          <Card className="p-0 hidden md:block overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-gray-500 border-b border-white/10">
                  <th className="text-left font-bold px-4 py-3">{t("Anfrage")}</th>
                  <th className="text-left font-bold px-4 py-3">{t("Status")}</th>
                  <th className="text-left font-bold px-4 py-3">{t("Ergebnis (intern)")}</th>
                  <th className="text-left font-bold px-4 py-3">{t("Priorität")}</th>
                  <th className="text-left font-bold px-4 py-3">{t("Datum")}</th>
                  <th className="text-right font-bold px-4 py-3">{t("Aktionen")}</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((l) => {
                  const procKeys = cfg.project_types?.[l.project_type]?.process || [];
                  return (
                    <tr key={l.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`lead-row-${l.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold">{l.name}</div>
                        <div className="text-xs text-gray-500">{l.company_name} · {l.project_label}</div>
                        <div className="text-xs text-gray-400 mt-0.5 truncate max-w-xs">{l.subject || l.message}</div>
                      </td>
                      <td className="px-4 py-3">
                        <select value={l.status} onChange={(e) => changeStatus(l, e.target.value)} data-testid={`lead-status-${l.id}`}
                          className="bg-black border border-white/10 rounded-lg px-2.5 py-1.5 text-xs focus:outline-none focus:border-[#A3E635]">
                          {procKeys.map((s) => <option key={s} value={s}>{plabel(s)}</option>)}
                        </select>
                      </td>
                      <td className="px-4 py-3"><span className="text-xs text-gray-300">{rlabel(l.result_status)}</span></td>
                      <td className="px-4 py-3"><PrioPill p={l.priority} /></td>
                      <td className="px-4 py-3 text-xs text-gray-400">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <button onClick={() => quickTask(l)} data-testid={`lead-quicktask-${l.id}`} title={t("Aufgabe erstellen")}
                            className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-gray-300"><KanbanSquare className="w-4 h-4" /></button>
                          <button onClick={() => nav(`/admin/leads/${l.id}`)} data-testid={`lead-open-${l.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#A3E635]/10 hover:bg-[#A3E635]/20 text-[#A3E635] text-xs font-bold">
                            {t("Öffnen")} <ArrowRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {/* Mobile-Karten */}
          <div className="md:hidden space-y-3">
            {shown.map((l) => (
              <Card key={l.id} className="p-4" data-testid={`lead-card-${l.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-sm">{l.name}</div>
                    <div className="text-xs text-gray-500">{l.company_name}</div>
                  </div>
                  <PrioPill p={l.priority} />
                </div>
                <div className="mt-1.5"><TypePill type={l.lead_type} label={t(l.lead_type_label || l.lead_type)} /></div>
                <div className="text-sm text-gray-300 mt-2">{l.subject || l.message}</div>
                <div className="flex items-center gap-2 mt-3 flex-wrap">
                  <StatusPill status={l.status} label={plabel(l.status)} />
                  <span className="text-[11px] text-gray-500">{fmtDate(l.created_at)}</span>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <button onClick={() => quickTask(l)} data-testid={`lead-card-task-${l.id}`}
                    className="py-2 rounded-lg bg-white/5 hover:bg-white/10 text-xs font-bold text-gray-200 inline-flex items-center justify-center gap-1.5"><KanbanSquare className="w-3.5 h-3.5" /> {t("Aufgabe")}</button>
                  <button onClick={() => nav(`/admin/leads/${l.id}`)} data-testid={`lead-card-open-${l.id}`}
                    className="py-2 rounded-lg bg-[#A3E635] text-black text-xs font-bold inline-flex items-center justify-center gap-1.5">{t("Öffnen")} <ArrowRight className="w-3.5 h-3.5" /></button>
                </div>
              </Card>
            ))}
          </div>
        </>
      )}

      {/* Neue Anfrage Modal */}
      {showNew && (
        <Dialog open onOpenChange={(open) => { if (!open && !savingRef.current) setShowNew(false); }}>
          <DialogContent data-testid="new-lead-modal" closeLabel={t("Schliessen")} closeDisabled={saving}
            className="w-[calc(100vw_-_2rem)] max-w-2xl max-h-[90dvh] overflow-y-auto min-w-0 bg-[#121217] border-white/10 text-white rounded-2xl p-4 sm:p-6"
            onOpenAutoFocus={(e) => { e.preventDefault(); leadTypeInput.current?.focus(); }}
            onCloseAutoFocus={(e) => { e.preventDefault(); newLeadBtnRef.current?.focus(); }}
            onEscapeKeyDown={(e) => { if (savingRef.current) e.preventDefault(); }}
            onInteractOutside={(e) => { if (savingRef.current) e.preventDefault(); }}>
            <div className="pr-8">
              <DialogTitle className="text-lg font-bold flex items-center gap-2"><Inbox className="w-5 h-5 text-[#A3E635]" /> {t("Neue Anfrage erfassen")}</DialogTitle>
              <DialogDescription className="text-sm text-gray-400 mt-1">{t("Erfassen Sie eine neue Anfrage für einen Kunden oder einen Neukunden-Lead.")}</DialogDescription>
            </div>
            <fieldset disabled={saving} className="grid sm:grid-cols-2 gap-3 mt-4">
              <Lbl t={t("Anfrage-Typ *")} full>
                <select ref={leadTypeInput} value={form.lead_type} onChange={(e) => setForm({ ...form, lead_type: e.target.value, status: "neu" })} data-testid="lead-type" className={inputCls}>
                  {Object.entries(cfg.lead_types).map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
                </select>
              </Lbl>
              {isNeu ? (
                <Lbl t={t("Firma / Interessent *")}>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="lead-company" className={inputCls} placeholder={t("z. B. Bäckerei Muster GmbH")} />
                </Lbl>
              ) : (
                <Lbl t={t("Kunde / Projekt *")}>
                  <select value={form.client_id} onChange={(e) => onClient(e.target.value)} data-testid="lead-client" className={inputCls}>
                    <option value="">{t("— wählen —")}</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
                  </select>
                </Lbl>
              )}
              <Lbl t={t("Name *")}><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" className={inputCls} placeholder={t("z. B. Familie Brönnimann")} /></Lbl>
              <Lbl t={t("E-Mail")}><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lead-email" className={inputCls} placeholder="name@firma.ch" /></Lbl>
              <Lbl t={t("Telefon")}><input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" className={inputCls} placeholder="+41 ..." /></Lbl>
              <Lbl t={t("Betreff *")} full><input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="lead-subject" className={inputCls} placeholder={t("Worum geht es?")} /></Lbl>
              <Lbl t={t("Nachricht *")} full><textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="lead-message" rows={3} className={inputCls} placeholder={t("Details zur Anfrage...")} /></Lbl>
              <Lbl t={t("Bearbeitungsstatus *")}>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="lead-form-status" className={inputCls}>
                  {formProcessKeys.map((s) => <option key={s} value={s}>{plabel(s)}</option>)}
                </select>
              </Lbl>
              <Lbl t={t("Priorität *")}>
                <select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="lead-form-priority" className={inputCls}>
                  {PRIORITIES.map(([k, v]) => <option key={k} value={k}>{t(v)}</option>)}
                </select>
              </Lbl>
              <Lbl t={t("Quelle")}>
                <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} data-testid="lead-source" className={inputCls}>
                  <option value="">{t("— wählen —")}</option>
                  {cfg.sources.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Lbl>
              <Lbl t={t("Kampagne (Brevo / Marketing)")}><input value={form.campaign} onChange={(e) => setForm({ ...form, campaign: e.target.value })} data-testid="lead-campaign" className={inputCls} placeholder={t("z. B. Frühling2026")} /></Lbl>
              <Lbl t={t("Interesse")}><input value={form.interest} onChange={(e) => setForm({ ...form, interest: e.target.value })} data-testid="lead-interest" className={inputCls} placeholder={t("z. B. Website + Cockpit")} /></Lbl>
              {form.source === "Brevo" && <Lbl t={t("Brevo-Kontakt-ID (optional)")}><input value={form.brevo_contact_id} onChange={(e) => setForm({ ...form, brevo_contact_id: e.target.value })} data-testid="lead-brevo-id" className={inputCls} /></Lbl>}
              {form.source === "Brevo" && <Lbl t={t("Brevo-Liste (optional)")}><input value={form.brevo_list} onChange={(e) => setForm({ ...form, brevo_list: e.target.value })} data-testid="lead-brevo-list" className={inputCls} /></Lbl>}
              <Lbl t={t("Gewünschter Rückruf")}><input value={form.callback_wanted} onChange={(e) => setForm({ ...form, callback_wanted: e.target.value })} data-testid="lead-callback" className={inputCls} placeholder={t("z. B. Vormittags")} /></Lbl>
              <Lbl t={t("Fälligkeit")}><input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="lead-due" className={inputCls} /></Lbl>
              <Lbl t={t("Zuständige Person")}><input value={form.assignee} onChange={(e) => setForm({ ...form, assignee: e.target.value })} data-testid="lead-assignee" className={inputCls} placeholder={t("z. B. Sandra")} /></Lbl>
              <Lbl t={t("Mögliche Leistung / Modul (intern)")}><input value={form.possible_service} onChange={(e) => setForm({ ...form, possible_service: e.target.value })} data-testid="lead-possible-service" className={inputCls} placeholder={t("z. B. SEO-Booster")} /></Lbl>
              <Lbl t={t("Geschätzter Wert CHF (intern)")}><input type="number" min="0" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} data-testid="lead-estimated-value" className={inputCls} placeholder="0" /></Lbl>
              <Lbl t={t("Ergebnisstatus intern")}>
                <select value={form.result_status} onChange={(e) => setForm({ ...form, result_status: e.target.value })} data-testid="lead-result-status" className={inputCls}>
                  {formResultKeys.map((s) => <option key={s} value={s}>{rlabel(s)}</option>)}
                </select>
              </Lbl>
              <Lbl t={t("Interne Notiz")} full><textarea value={form.internal_note} onChange={(e) => setForm({ ...form, internal_note: e.target.value })} data-testid="lead-internal-note" rows={2} className={inputCls} placeholder={t("Nur für KEWAG sichtbar...")} /></Lbl>
            </fieldset>

            {(errs.length > 0 || warns.length > 0) && (
              <div className="mt-4 space-y-1.5" data-testid="lead-form-warnings">
                {errs.map((e, i) => <div key={`e${i}`} className="text-sm text-red-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {e}</div>)}
                {warns.map((w, i) => <div key={`w${i}`} className="text-sm text-amber-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {w}</div>)}
              </div>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowNew(false)} disabled={saving} data-testid="lead-cancel" className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 disabled:opacity-40">{t("Abbrechen")}</button>
              <button onClick={save} disabled={saving || errs.length > 0} data-testid="lead-save"
                className="bg-[#A3E635] hover:bg-[#BEF264] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold py-2 px-5 rounded-lg transition-colors">
                {saving ? t("Speichern…") : t("Anfrage speichern")}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

const Lbl = ({ t, children, full }) => {
  const id = children.props["data-testid"] || children.props.id;
  const control = id ? React.cloneElement(children, { id }) : children;
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label htmlFor={id} className="text-[11px] font-bold uppercase tracking-wider text-gray-400">{t}</label>
      <div className="mt-1">{control}</div>
    </div>
  );
};
